import React from 'react';
import moment from 'moment-timezone';
import { useHistory } from 'react-router-dom';
import { appRoutes } from '@/routes';
import { Accordion } from '@/components/Accordion';
import { Table } from '@/components/Table';
import { Button, buttonVariants } from '@/components/Button';
import {
  TotalTimeTrackedCard,
  ActiveTimeCard,
  ProductivityScoreCard,
  IdleTimeCard,
  IdlePercentCard,
  ScreenshotsCard,
} from '@/components/StatsCards';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { formatPercent } from '@/utils/numberUtils';
import { formatTimeFromSeconds } from '@/utils/timeUtils';
import { normalizeIdleForDisplay } from '@/utils/idleDisplayUtils';
import { mergeReportFiltersIntoSearch } from '@/utils/reportFiltersUrl';
import { getActivityTextColor } from '@/utils/activityColor';
import { TimelineChart } from '../Chart';
import { DateBadgeComponent as DateBadge } from './DateBadge.component';
import styles from './DayTimelineCard.module.scss';

export const DayTimelineCardComponent = ({
  day,
  index,
  segments,
  memberId,
  tableColumns,
}) => {
  const history = useHistory();
  const date = moment(day.dayKey);
  const { displayIdle, idlePercent } = normalizeIdleForDisplay(
    day.totals.durationSec,
    day.totals.idleSec
  );

  // Opens Dashboard Overview for this member on this specific day, instead
  // of toggling the accordion (which the click would otherwise bubble up to).
  const handleViewDayDetails = (event) => {
    event.stopPropagation();
    history.push({
      pathname: appRoutes().dashboardOverviewNew,
      search: mergeReportFiltersIntoSearch('', {
        memberId,
        start: day.dayKey,
        end: day.dayKey,
      }),
    });
  };

  return (
    <div className={styles.dayCard}>
      <Accordion
        isOpenOnStart={index === 0}
        showArrow={false}
        headingClass={styles.dayCardHeadingWrapper}
        contentClassName={styles.dayCardContent}
        heading={
          <div className={styles.dayCardHeading}>
            <div className={styles.dayCardTopRow}>
              <div className={styles.dayCardTopLeft}>
                <DateBadge date={date} colorIndex={date.dayOfYear()} />
                <div className={styles.dayCardHeadingText}>
                  <Text
                    variant={textVariantsEnum.Display_xs}
                    fontWeight={fontWeightEnum.Semibold}
                    color="gray-900"
                  >
                    {date.format('dddd, MMM D, YYYY')}
                  </Text>
                  <Text
                    variant={textVariantsEnum.Text_sm}
                    fontWeight={fontWeightEnum.Normal}
                    color="gray-500"
                  >
                    {`Total: ${formatTimeFromSeconds(day.totals.durationSec)}`}
                    <span className={styles.summaryDivider}>•</span>
                    {`Idle: ${formatTimeFromSeconds(displayIdle)}`}
                    <span className={styles.summaryDivider}>•</span>
                    {`Activity: ${formatPercent(day.totals.activityPct)}`}
                    {day.totals.earnings != null && (
                      <>
                        <span className={styles.summaryDivider}>•</span>
                        {`Earnings: $${day.totals.earnings}`}
                      </>
                    )}
                  </Text>
                </div>
              </div>
              <div className={styles.viewDetailsGroup}>
                <Button
                  variant={buttonVariants.primary_outline}
                  size="sm"
                  text="View Day Report"
                  onClick={handleViewDayDetails}
                />
              </div>
            </div>
            <TimelineChart segments={segments} />
          </div>
        }
      >
        <div className={styles.statsRow}>
          <TotalTimeTrackedCard
            totalTimeTracked={formatTimeFromSeconds(day.totals.durationSec)}
            contaninerClassName={styles.statTile}
            compact
          />
          <ActiveTimeCard
            activeTime={formatTimeFromSeconds(day.totals.productiveSec)}
            contaninerClassName={styles.statTile}
          />
          <ProductivityScoreCard
            productivityScore={formatPercent(day.totals.activityPct)}
            valueColor={getActivityTextColor(day.totals.activityPct)}
            contaninerClassName={styles.statTile}
            compact
          />
          <IdleTimeCard
            idleTime={formatTimeFromSeconds(displayIdle)}
            contaninerClassName={styles.statTile}
            compact
          />
          <IdlePercentCard
            idlePercent={formatPercent(idlePercent)}
            contaninerClassName={styles.statTile}
          />
          <ScreenshotsCard
            screenshotsLabel={String(
              day.sessions.reduce(
                (sum, session) => sum + (session.screenshotsCount || 0),
                0
              )
            )}
            contaninerClassName={styles.statTile}
          />
        </div>
        <div className={styles.tableContainer}>
          <Table
            data={day.sessions}
            columns={tableColumns}
            className={styles.timelineTable}
          />
        </div>
      </Accordion>
    </div>
  );
};

DayTimelineCardComponent.displayName = 'DayTimelineCard';
